import React, { useState, useRef, useCallback, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { CANVAS_W, CANVAS_H } from '../constants';

// ── Bezier math helpers ───────────────────────────────────────────────────────

/** Evaluate a cubic bezier at t → {x, y} (in % coords) */
function cubicBezierAt(p0, cp1, cp2, p1, t) {
  const mt = 1 - t;
  return {
    x: mt*mt*mt*p0.x + 3*mt*mt*t*cp1.x + 3*mt*t*t*cp2.x + t*t*t*p1.x,
    y: mt*mt*mt*p0.y + 3*mt*mt*t*cp1.y + 3*mt*t*t*cp2.y + t*t*t*p1.y,
  };
}

/** Tangent direction of cubic bezier at t (unnormalized) */
function cubicBezierTangent(p0, cp1, cp2, p1, t) {
  const mt = 1 - t;
  return {
    x: 3*(mt*mt*(cp1.x-p0.x) + 2*mt*t*(cp2.x-cp1.x) + t*t*(p1.x-cp2.x)),
    y: 3*(mt*mt*(cp1.y-p0.y) + 2*mt*t*(cp2.y-cp1.y) + t*t*(p1.y-cp2.y)),
  };
}

/**
 * Find the closest point on the path (segments between consecutive points)
 * to a mouse position. Returns { segIdx, t, dist, pos } in % coords.
 * dist is in SVG pixels.
 */
function closestPointOnPath(points, closed, mousePercent) {
  const toSVG = p => ({ x: (p.x / 100) * CANVAS_W, y: (p.y / 100) * CANVAS_H });
  const mSVG = toSVG(mousePercent);
  const len = points.length;
  const segCount = closed ? len : len - 1;

  let best = null;

  for (let i = 0; i < segCount; i++) {
    const prev = points[i];
    const next = points[(i + 1) % len];
    const cp1 = prev.cp2 || prev;
    const cp2 = next.cp1 || next;
    const hasCurve = prev.cp2 || next.cp1;

    // Sample the segment
    const STEPS = 50;
    for (let s = 0; s <= STEPS; s++) {
      const t = s / STEPS;
      let pos;
      if (hasCurve) {
        pos = cubicBezierAt(prev, cp1, cp2, next, t);
      } else {
        pos = { x: prev.x + (next.x - prev.x) * t, y: prev.y + (next.y - prev.y) * t };
      }
      const pSVG = toSVG(pos);
      const dist = Math.hypot(pSVG.x - mSVG.x, pSVG.y - mSVG.y);
      if (!best || dist < best.dist) {
        best = { segIdx: i, t, dist, pos };
      }
    }
  }
  return best;
}

/**
 * Split segment at t using De Casteljau, producing a new anchor with tangents
 * and updated tangents for the two neighbours. Returns:
 *   { newPoint, updatedPrev, updatedNext }
 */
function splitSegmentAtT(prev, next, t) {
  const cp1 = prev.cp2 || prev;
  const cp2 = next.cp1 || next;
  const hasCurve = prev.cp2 || next.cp1;

  if (!hasCurve) {
    // Linear — new point, no tangents
    const newPoint = {
      x: prev.x + (next.x - prev.x) * t,
      y: prev.y + (next.y - prev.y) * t,
    };
    return {
      newPoint,
      updatedPrev: { ...prev },
      updatedNext: { ...next },
    };
  }

  // De Casteljau subdivision
  const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x)*t, y: a.y + (b.y - a.y)*t });
  const p01  = lerp(prev,  cp1,  t);
  const p12  = lerp(cp1,   cp2,  t);
  const p23  = lerp(cp2,   next, t);
  const p012 = lerp(p01,  p12,  t);
  const p123 = lerp(p12,  p23,  t);
  const p0123 = lerp(p012, p123, t);

  return {
    newPoint:    { ...p0123, cp1: p012, cp2: p123 },
    updatedPrev: { ...prev,  cp2: p01  },
    updatedNext: { ...next,  cp1: p23  },
  };
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ACCENT       = '#1075BA';
const CLOSE_RADIUS = 10; // px in SVG space — how close to first point to close path
const POINT_R      = 5;  // anchor point radius
const TANGENT_R    = 4;  // tangent handle radius

// ── Bezier helpers ────────────────────────────────────────────────────────────

/** Convert a list of PenPoints into an SVG path `d` string. */
function pointsToPath(points, closed = false, mousePos = null) {
  if (points.length === 0) return '';
  const toSVG = p => `${(p.x / 100) * CANVAS_W},${(p.y / 100) * CANVAS_H}`;
  const tx = p => (p.x / 100) * CANVAS_W;
  const ty = p => (p.y / 100) * CANVAS_H;

  let d = `M ${toSVG(points[0])}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cp1  = prev.cp2 || prev;  // exit tangent of previous
    const cp2  = curr.cp1 || curr;  // entry tangent of current
    const hasCurve = prev.cp2 || curr.cp1;
    if (hasCurve) {
      d += ` C ${tx(cp1)},${ty(cp1)} ${tx(cp2)},${ty(cp2)} ${tx(curr)},${ty(curr)}`;
    } else {
      d += ` L ${toSVG(curr)}`;
    }
  }

  // Preview segment to mouse
  if (!closed && mousePos && points.length > 0) {
    const last  = points[points.length - 1];
    const cp1   = last.cp2 || last;
    const hasCurve = !!last.cp2;
    if (hasCurve) {
      d += ` C ${tx(cp1)},${ty(cp1)} ${tx(mousePos)},${ty(mousePos)} ${tx(mousePos)},${ty(mousePos)}`;
    } else {
      d += ` L ${(mousePos.x / 100) * CANVAS_W},${(mousePos.y / 100) * CANVAS_H}`;
    }
  }

  if (closed) d += ' Z';
  return d;
}

/**
 * Each point in the path:
 * {
 *   x, y          — anchor in % coords
 *   cp1: {x,y}   — entry control point (optional, % coords)
 *   cp2: {x,y}   — exit  control point (optional, % coords)
 * }
 *
 * When the user drags on placement, cp1 and cp2 are set symmetrically.
 * cp1 is the mirror of cp2 about the anchor.
 */

// ── PenTool SVG layer ─────────────────────────────────────────────────────────
/**
 * Rendered inside the main SVG overlay while the pen tool is active.
 *
 * Props:
 *   points        — current path points array
 *   mousePos      — current canvas-% cursor position (or null)
 *   onSvgMouseDown  — forwarded mousedown on the SVG background
 *   onSvgMouseMove  — forwarded mousemove on the SVG background
 *   onPointMouseDown — (e, idx) mousedown on an anchor
 *   onTangentMouseDown — (e, idx, 'cp1'|'cp2') mousedown on a tangent handle
 *   onFirstPointClick  — () user clicked the first point (close path)
 *   isCloseable     — whether we're hovering near the first point
 */
export function PenToolSVGLayer({
  points,
  mousePos,
  isCloseable,
  onSvgMouseDown,
  onSvgMouseMove,
  onPointMouseDown,
  onTangentMouseDown,
  onFirstPointClick,
}) {
  const tx = p => (p.x / 100) * CANVAS_W;
  const ty = p => (p.y / 100) * CANVAS_H;

  const pathD = pointsToPath(points, false, mousePos);

  return (
    <g style={{ pointerEvents: 'all' }}>
      {/* Transparent hit area for the whole SVG */}
      <rect
        x={0} y={0} width={CANVAS_W} height={CANVAS_H}
        fill="transparent"
        onMouseDown={onSvgMouseDown}
        onMouseMove={onSvgMouseMove}
        style={{ cursor: 'crosshair' }}
      />

      {/* Path preview */}
      {pathD && (
        <path
          d={pathD}
          fill="none"
          stroke={ACCENT}
          strokeWidth={1.5}
          strokeDasharray="none"
          pointerEvents="none"
        />
      )}

      {/* Tangent lines + handles */}
      {points.map((pt, i) => (
        <g key={`tangents-${i}`}>
          {pt.cp1 && (
            <>
              <line
                x1={tx(pt)} y1={ty(pt)} x2={tx(pt.cp1)} y2={ty(pt.cp1)}
                stroke={alpha(ACCENT, 0.5)} strokeWidth={1} pointerEvents="none"
              />
              <circle
                cx={tx(pt.cp1)} cy={ty(pt.cp1)} r={TANGENT_R}
                fill="#fff" stroke={ACCENT} strokeWidth={1.5}
                style={{ cursor: 'move' }}
                onMouseDown={e => { e.stopPropagation(); onTangentMouseDown(e, i, 'cp1'); }}
              />
            </>
          )}
          {pt.cp2 && (
            <>
              <line
                x1={tx(pt)} y1={ty(pt)} x2={tx(pt.cp2)} y2={ty(pt.cp2)}
                stroke={alpha(ACCENT, 0.5)} strokeWidth={1} pointerEvents="none"
              />
              <circle
                cx={tx(pt.cp2)} cy={ty(pt.cp2)} r={TANGENT_R}
                fill="#fff" stroke={ACCENT} strokeWidth={1.5}
                style={{ cursor: 'move' }}
                onMouseDown={e => { e.stopPropagation(); onTangentMouseDown(e, i, 'cp2'); }}
              />
            </>
          )}
        </g>
      ))}

      {/* Anchor points */}
      {points.map((pt, i) => {
        const isFirst  = i === 0;
        const isClose  = isFirst && isCloseable && points.length > 2;
        return (
          <circle
            key={`pt-${i}`}
            cx={tx(pt)} cy={ty(pt)} r={isClose ? POINT_R + 3 : POINT_R}
            fill={isClose ? ACCENT : '#fff'}
            stroke={ACCENT} strokeWidth={2}
            style={{ cursor: isClose ? 'cell' : 'move' }}
            onMouseDown={e => {
              e.stopPropagation();
              if (isClose && points.length > 2) { onFirstPointClick(); return; }
              onPointMouseDown(e, i);
            }}
          />
        );
      })}
    </g>
  );
}

// ── usePenTool hook ───────────────────────────────────────────────────────────
/**
 * All pen-tool interaction state and logic, isolated from the rest of the builder.
 *
 * getCanvasCoords(mouseEvent) → {x,y} in % — must be passed in from the parent
 *   (it needs the canvasRef DOM rect).
 *
 * onCommit(hotspot) — called when the path is closed; receives a ready-to-add hotspot.
 */
export function usePenTool({ getCanvasCoords, onCommit }) {
  const [active,    setActive]    = useState(false); // pen tool toggled on
  const [points,    setPoints]    = useState([]);    // PenPoint[]
  const [mousePos,  setMousePos]  = useState(null);  // {x,y} % — live cursor
  const [isCloseable, setCloseable] = useState(false);

  // Drag state stored in a ref so mousemove callbacks are always fresh
  const drag = useRef(null);
  // {
  //   kind: 'place'    — user is dragging the point they just placed (setting cp2)
  //   kind: 'move'     — dragging an existing anchor
  //   kind: 'tangent'  — dragging a cp1/cp2 handle
  //   idx, cpKey
  // }

  const reset = useCallback(() => {
    setPoints([]);
    setMousePos(null);
    setCloseable(false);
    drag.current = null;
  }, []);

  const activate   = useCallback(() => { setActive(true);  reset(); }, [reset]);
  const deactivate = useCallback(() => { setActive(false); reset(); }, [reset]);

  // ── SVG mouse move ───────────────────────────────────────────────────────
  const handleSvgMouseMove = useCallback((e) => {
    const pos = getCanvasCoords(e);
    if (!pos) return;
    setMousePos(pos);

    // Update drag
    if (drag.current) {
      const { kind, idx, cpKey, startPos, originalPoint } = drag.current;

      if (kind === 'place') {
        // Dragging immediately after placing a point → set cp2 (exit tangent)
        // and mirror cp1 (entry tangent) symmetrically
        const dx = pos.x - originalPoint.x;
        const dy = pos.y - originalPoint.y;
        setPoints(prev => prev.map((p, i) => {
          if (i !== idx) return p;
          const cp2 = { x: p.x + dx, y: p.y + dy };
          const cp1 = { x: p.x - dx, y: p.y - dy };
          return { ...p, cp1, cp2 };
        }));
      }

      if (kind === 'move') {
        const dx = pos.x - startPos.x;
        const dy = pos.y - startPos.y;
        setPoints(prev => prev.map((p, i) => {
          if (i !== idx) return p;
          const moved = { ...p, x: p.x + dx, y: p.y + dy };
          if (p.cp1) moved.cp1 = { x: p.cp1.x + dx, y: p.cp1.y + dy };
          if (p.cp2) moved.cp2 = { x: p.cp2.x + dx, y: p.cp2.y + dy };
          return moved;
        }));
        drag.current.startPos = pos; // incremental delta
      }

      if (kind === 'tangent') {
        // Moving one tangent handle — the opposite handle mirrors unless shift is held
        setPoints(prev => prev.map((p, i) => {
          if (i !== idx) return p;
          const updated = { ...p, [cpKey]: { x: pos.x, y: pos.y } };
          // Mirror the opposite tangent about the anchor
          const opposite = cpKey === 'cp1' ? 'cp2' : 'cp1';
          if (p[opposite] && !e.shiftKey) {
            const dx = pos.x - p.x;
            const dy = pos.y - p.y;
            const dist = Math.hypot(dx, dy);
            const oDist = Math.hypot(p[opposite].x - p.x, p[opposite].y - p.y);
            // Same distance, opposite direction
            if (dist > 0) {
              updated[opposite] = {
                x: p.x - dx / dist * oDist,
                y: p.y - dy / dist * oDist,
              };
            }
          }
          return updated;
        }));
      }
    }

    // Closeable check
    if (points.length > 2) {
      const first = points[0];
      const dpx = (pos.x - first.x) / 100 * CANVAS_W;
      const dpy = (pos.y - first.y) / 100 * CANVAS_H;
      setCloseable(Math.hypot(dpx, dpy) < CLOSE_RADIUS);
    }
  }, [getCanvasCoords, points]);

  // ── Place a new point (click on SVG background) ──────────────────────────
  const handleSvgMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const pos = getCanvasCoords(e);
    if (!pos) return;

    const newPoint = { x: pos.x, y: pos.y };
    const idx = points.length;
    setPoints(prev => [...prev, newPoint]);
    drag.current = { kind: 'place', idx, startPos: pos, originalPoint: newPoint };
  }, [getCanvasCoords, points.length]);

  // ── Mouse up — end drag ──────────────────────────────────────────────────
  const handleMouseUp = useCallback(() => {
    drag.current = null;
  }, []);

  // ── Drag an existing anchor ──────────────────────────────────────────────
  const handlePointMouseDown = useCallback((e, idx) => {
    e.stopPropagation();
    const pos = getCanvasCoords(e);
    drag.current = { kind: 'move', idx, startPos: pos };
  }, [getCanvasCoords]);

  // ── Drag a tangent handle ────────────────────────────────────────────────
  const handleTangentMouseDown = useCallback((e, idx, cpKey) => {
    e.stopPropagation();
    drag.current = { kind: 'tangent', idx, cpKey };
  }, []);

  // ── Close path → commit as hotspot ──────────────────────────────────────
  const handleFirstPointClick = useCallback(() => {
    if (points.length < 3) return;

    // Build SVG path d-string for the closed shape
    const pathD = pointsToPath(points, true);

    // Build hotspot in the polygon schema format (plain % points for AABB storage)
    // For bezier paths we store the full cp data in meta.bezierPoints
    // and use a simplified polygon (anchors only) as the fallback `points` array.
    const hotspot = {
      shape:      'polygon',
      points:     points.map(p => ({ x: p.x, y: p.y })),
      meta: {
        isBezier:      true,
        bezierPoints:  points,   // full cp1/cp2 data
        pathD,
      },
      label:      '',
      action:     { type: 'page', pageId: null, url: null },
      glowColor:  '#1075BA',
      glowOpacity: 0.5,
      isActive:   true,
    };

    onCommit(hotspot);
    reset();
  }, [points, onCommit, reset]);

  // Attach global mouseup
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  return {
    active,
    activate,
    deactivate,
    points,
    mousePos,
    isCloseable,
    handleSvgMouseDown,
    handleSvgMouseMove,
    handlePointMouseDown,
    handleTangentMouseDown,
    handleFirstPointClick,
    reset,
  };
}

// ── PathEditSVGLayer ──────────────────────────────────────────────────────────
/**
 * Overlay rendered when the user double-clicks an existing bezier path to edit it.
 *
 * Props:
 *   hotspot          — the hotspot being edited
 *   mousePos         — current canvas-% cursor position (or null)
 *   selectedPtIdx    — index of selected anchor (or null)
 *   hoverSegment     — { segIdx, t, pos } or null  (add-point preview)
 *   onBgMouseDown    — mousedown on the transparent background rect
 *   onBgMouseMove    — mousemove on the background rect
 *   onPointMouseDown — (e, idx)
 *   onTangentMouseDown — (e, idx, 'cp1'|'cp2')
 *   onAddPointClick  — (segIdx, t)
 */
export function PathEditSVGLayer({
  pts,
  selectedPtIdx,
  hoverSegment,
  onBgMouseDown,
  onBgMouseMove,
  onPointMouseDown,
  onTangentMouseDown,
  onAddPointClick,
}) {
  if (!pts || pts.length === 0) return null;
  const closed = true;

  const tx = p => (p.x / 100) * CANVAS_W;
  const ty = p => (p.y / 100) * CANVAS_H;
  const pathD = pointsToPath(pts, closed);

  return (
    <g style={{ pointerEvents: 'all' }}>
      {/* Transparent hit area */}
      <rect
        x={0} y={0} width={CANVAS_W} height={CANVAS_H}
        fill="transparent"
        onMouseDown={onBgMouseDown}
        onMouseMove={onBgMouseMove}
        style={{ cursor: 'default' }}
      />

      {/* The path itself (solid while editing) */}
      {pathD && (
        <path
          d={pathD}
          fill={alpha(ACCENT, 0.12)}
          stroke={ACCENT}
          strokeWidth={1.5}
          pointerEvents="none"
        />
      )}

      {/* Tangent lines + handles */}
      {pts.map((pt, i) => (
        <g key={`edit-tangents-${i}`}>
          {pt.cp1 && (
            <>
              <line
                x1={tx(pt)} y1={ty(pt)} x2={tx(pt.cp1)} y2={ty(pt.cp1)}
                stroke={alpha(ACCENT, 0.5)} strokeWidth={1} pointerEvents="none"
              />
              <circle
                cx={tx(pt.cp1)} cy={ty(pt.cp1)} r={TANGENT_R}
                fill="#fff" stroke={ACCENT} strokeWidth={1.5}
                style={{ cursor: 'move' }}
                onMouseDown={e => { e.stopPropagation(); onTangentMouseDown(e, i, 'cp1'); }}
              />
            </>
          )}
          {pt.cp2 && (
            <>
              <line
                x1={tx(pt)} y1={ty(pt)} x2={tx(pt.cp2)} y2={ty(pt.cp2)}
                stroke={alpha(ACCENT, 0.5)} strokeWidth={1} pointerEvents="none"
              />
              <circle
                cx={tx(pt.cp2)} cy={ty(pt.cp2)} r={TANGENT_R}
                fill="#fff" stroke={ACCENT} strokeWidth={1.5}
                style={{ cursor: 'move' }}
                onMouseDown={e => { e.stopPropagation(); onTangentMouseDown(e, i, 'cp2'); }}
              />
            </>
          )}
        </g>
      ))}

      {/* Anchor points */}
      {pts.map((pt, i) => {
        const isSelected = i === selectedPtIdx;
        return (
          <circle
            key={`edit-pt-${i}`}
            cx={tx(pt)} cy={ty(pt)}
            r={isSelected ? POINT_R + 2 : POINT_R}
            fill={isSelected ? ACCENT : '#fff'}
            stroke={ACCENT} strokeWidth={2}
            style={{ cursor: 'move' }}
            onMouseDown={e => { e.stopPropagation(); onPointMouseDown(e, i); }}
          />
        );
      })}

      {/* Add-point preview: highlight near the path */}
      {hoverSegment && hoverSegment.dist < 12 && (
        <circle
          cx={tx(hoverSegment.pos)} cy={ty(hoverSegment.pos)}
          r={POINT_R}
          fill={ACCENT} stroke="#fff" strokeWidth={2}
          style={{ cursor: 'copy', pointerEvents: 'all' }}
          onMouseDown={e => {
            e.stopPropagation();
            onAddPointClick(hoverSegment.segIdx, hoverSegment.t);
          }}
        />
      )}
    </g>
  );
}

// ── usePathEdit hook ──────────────────────────────────────────────────────────
/**
 * All state & logic for editing an existing bezier hotspot.
 *
 * getCanvasCoords(e) → {x,y}%   from parent
 * hotspot                        the hotspot being edited (or null)
 * onCommit(updatedHotspot)       called on every point mutation
 * onExit()                       called when editing should stop
 */
export function usePathEdit({ getCanvasCoords, hotspot, onCommit, onExit }) {
  // Working copy of bezier points — source of truth for the SVG layer
  const [pts, setPts] = useState([]);
  const [selectedPtIdx, setSelectedPtIdx] = useState(null);
  const [hoverSegment, setHoverSegment] = useState(null);
  const drag = useRef(null);

  // Keep a ref to the latest hotspot so commit() never goes stale
  const hotspotRef = useRef(hotspot);
  useEffect(() => { hotspotRef.current = hotspot; }, [hotspot]);

  // Only re-seed pts when the hotspot ID changes (i.e. a different shape is entered).
  // Do NOT re-seed on every commit — that would fight the drag state.
  const editingIdRef = useRef(null);
  useEffect(() => {
    const id = hotspot?.id ?? null;
    if (id === editingIdRef.current) return;   // same shape, skip
    editingIdRef.current = id;
    if (!hotspot?.meta?.bezierPoints) { setPts([]); return; }
    setPts(hotspot.meta.bezierPoints.map(p => ({ ...p })));
    setSelectedPtIdx(null);
    setHoverSegment(null);
    drag.current = null;
  }, [hotspot?.id]); 

  // Commit helper — uses the ref so it's never stale
  const commit = useCallback((newPts) => {
    const h = hotspotRef.current;
    if (!h) return;
    const pathD = pointsToPath(newPts, true);
    onCommit({
      ...h,
      points: newPts.map(p => ({ x: p.x, y: p.y })),
      meta: { ...h.meta, bezierPoints: newPts, pathD },
    });
  }, [onCommit]);

  // ── Mouse move ────────────────────────────────────────────────────────────
  const handleBgMouseMove = useCallback((e) => {
    const pos = getCanvasCoords(e);
    if (!pos) return;

    if (drag.current) {
      const { kind, idx, cpKey, startPos } = drag.current;

      if (kind === 'move') {
        const dx = pos.x - startPos.x;
        const dy = pos.y - startPos.y;
        setPts(prev => {
          const next = prev.map((p, i) => {
            if (i !== idx) return p;
            const moved = { ...p, x: p.x + dx, y: p.y + dy };
            if (p.cp1) moved.cp1 = { x: p.cp1.x + dx, y: p.cp1.y + dy };
            if (p.cp2) moved.cp2 = { x: p.cp2.x + dx, y: p.cp2.y + dy };
            return moved;
          });
          commit(next);
          return next;
        });
        drag.current.startPos = pos;
      }

      if (kind === 'tangent') {
        setPts(prev => {
          const next = prev.map((p, i) => {
            if (i !== idx) return p;
            const updated = { ...p, [cpKey]: { x: pos.x, y: pos.y } };
            const opposite = cpKey === 'cp1' ? 'cp2' : 'cp1';
            if (p[opposite] && !e.shiftKey) {
              const dx = pos.x - p.x;
              const dy = pos.y - p.y;
              const dist = Math.hypot(dx, dy);
              const oDist = Math.hypot(p[opposite].x - p.x, p[opposite].y - p.y);
              if (dist > 0) {
                updated[opposite] = {
                  x: p.x - dx / dist * oDist,
                  y: p.y - dy / dist * oDist,
                };
              }
            }
            return updated;
          });
          commit(next);
          return next;
        });
      }
      return; // skip hover when dragging
    }

    // Hover: find closest point on path for add-point hint
    setPts(current => {
      if (current.length >= 2) {
        const seg = closestPointOnPath(current, true, pos);
        setHoverSegment(seg || null);
      }
      return current; // no mutation
    });
  }, [getCanvasCoords, commit]);

  // ── Background click (deselect) ───────────────────────────────────────────
  const handleBgMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    setSelectedPtIdx(null);
  }, []);

  // ── Anchor mousedown ──────────────────────────────────────────────────────
  const handlePointMouseDown = useCallback((e, idx) => {
    e.stopPropagation();
    const pos = getCanvasCoords(e);
    setSelectedPtIdx(idx);
    drag.current = { kind: 'move', idx, startPos: pos };
  }, [getCanvasCoords]);

  // ── Tangent mousedown ─────────────────────────────────────────────────────
  const handleTangentMouseDown = useCallback((e, idx, cpKey) => {
    e.stopPropagation();
    drag.current = { kind: 'tangent', idx, cpKey };
  }, []);

  // ── Mouse up ──────────────────────────────────────────────────────────────
  const handleMouseUp = useCallback(() => {
    drag.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  // ── Delete selected point (keyboard) ─────────────────────────────────────
  useEffect(() => {
    if (!hotspot) return;
    const onKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedPtIdx !== null) {
        if (pts.length <= 3) return; // keep minimum 3 points
        setPts(prev => {
          const next = prev.filter((_, i) => i !== selectedPtIdx);
          commit(next);
          return next;
        });
        setSelectedPtIdx(null);
      }
      if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hotspot, selectedPtIdx, pts, commit, onExit]);

  // ── Add point along path ──────────────────────────────────────────────────
  const handleAddPointClick = useCallback((segIdx, t) => {
    setPts(prev => {
      const len = prev.length;
      const prevPt = prev[segIdx];
      const nextPt = prev[(segIdx + 1) % len];
      const { newPoint, updatedPrev, updatedNext } = splitSegmentAtT(prevPt, nextPt, t);

      const next = [...prev];
      next[segIdx] = updatedPrev;
      next[(segIdx + 1) % len] = updatedNext;
      next.splice(segIdx + 1, 0, newPoint);
      commit(next);
      return next;
    });
  }, [commit]);

  return {
    pts,
    selectedPtIdx,
    hoverSegment,
    handleBgMouseDown,
    handleBgMouseMove,
    handlePointMouseDown,
    handleTangentMouseDown,
    handleAddPointClick,
  };
}